export const GA_MEASUREMENT_ID = 'G-3PB67SFJ3P';

/**
 * @returns {((...args: unknown[]) => void)|null}
 */
function gtag() {
  if (typeof window === 'undefined') {
    return null;
  }
  if (typeof window.gtag === 'function') {
    return window.gtag;
  }
  return null;
}

/**
 * @param {Record<string, unknown>} params
 */
function sendEvent(eventName, params = {}) {
  const fn = gtag();
  if (!fn) {
    return;
  }
  fn('event', eventName, params);
}

/**
 * @param {number} count
 * @returns {'0'|'1-10'|'11-50'|'51+'}
 */
export function bucketResultCount(count) {
  if (!Number.isFinite(count) || count <= 0) {
    return '0';
  }
  if (count <= 10) {
    return '1-10';
  }
  if (count <= 50) {
    return '11-50';
  }
  return '51+';
}

/**
 * @param {number|null|undefined} position
 * @returns {'1-3'|'4-12'|'13+'|'unknown'}
 */
export function bucketPosition(position) {
  if (position == null || !Number.isFinite(position) || position < 1) {
    return 'unknown';
  }
  if (position <= 3) {
    return '1-3';
  }
  if (position <= 12) {
    return '4-12';
  }
  return '13+';
}

/**
 * @param {string} pathname
 * @param {URLSearchParams} searchParams
 */
export function buildPageMetadata(pathname, searchParams) {
  const shopCategory = (searchParams.get('shop') ?? '').toLowerCase();
  const query = searchParams.get('q') ?? '';
  const saleMode = searchParams.get('sale') === '1';

  if (pathname === '/') {
    return { page_type: 'home' };
  }
  if (pathname === '/start') {
    return { page_type: 'pre_lander' };
  }
  if (pathname === '/search') {
    return {
      page_type: 'search',
      has_query: query.trim() !== '' ? 'true' : 'false',
      sale_mode: saleMode ? 'true' : 'false',
      ...(shopCategory ? { shop_category: shopCategory } : {}),
    };
  }
  if (pathname.startsWith('/shop/')) {
    const parts = pathname.slice('/shop/'.length).split('/').filter(Boolean);
    if (parts.length >= 2) {
      return {
        page_type: 'subcategory',
        shop_category: parts[0].toLowerCase(),
        subcategory_slug: parts[1].toLowerCase(),
      };
    }
    if (parts.length === 1) {
      return {
        page_type: 'category_hub',
        shop_category: parts[0].toLowerCase(),
      };
    }
  }
  if (pathname.startsWith('/browse/')) {
    const slug = pathname.slice('/browse/'.length).split('/')[0].toLowerCase();
    return {
      page_type: 'category_hub',
      shop_category: slug,
    };
  }
  if (pathname === '/about') {
    return { page_type: 'about' };
  }
  if (pathname === '/add-store') {
    return { page_type: 'add_store' };
  }
  if (pathname === '/add-store/apply') {
    return { page_type: 'apply_listing' };
  }
  return { page_type: 'other' };
}

/**
 * @param {string} pathname
 * @param {string} search
 */
export function trackPageView(pathname, search = '') {
  const fn = gtag();
  if (!fn) {
    return;
  }

  const searchParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const metadata = buildPageMetadata(pathname, searchParams);
  const pagePath = `${pathname}${search}`;

  fn('event', 'page_view', {
    page_title: document.title,
    page_location: `${window.location.origin}${pagePath}`,
    page_path: pagePath,
    ...metadata,
  });
}

/**
 * @param {string} searchTerm
 * @param {string} searchLocation
 */
export function trackSearch(searchTerm, searchLocation) {
  const term = typeof searchTerm === 'string' ? searchTerm.trim() : '';
  if (term === '') {
    return;
  }
  sendEvent('search', {
    search_term: term.length > 100 ? term.slice(0, 100) : term,
    search_location: searchLocation,
  });
}

export function trackSearchAutocompleteSelect() {
  sendEvent('search_autocomplete_select', {
    suggestion_type: 'run_search',
  });
}

/**
 * @param {{
 *   totalCount: number,
 *   resultsSource: string,
 *   pageNumber: number,
 *   hasActiveFilters: boolean,
 *   broadFallback: boolean,
 *   searchFallbackStep?: number|null,
 *   shopCategorySlug?: string,
 * }} params
 */
export function trackViewSearchResults({
  totalCount,
  resultsSource,
  pageNumber,
  hasActiveFilters,
  broadFallback,
  searchFallbackStep = null,
  shopCategorySlug = '',
}) {
  sendEvent('view_search_results', {
    result_count_bucket: bucketResultCount(totalCount),
    results_source: resultsSource,
    page_number: Math.max(1, pageNumber),
    has_active_filters: hasActiveFilters ? 'true' : 'false',
    broad_fallback: broadFallback ? 'true' : 'false',
    ...(searchFallbackStep != null ? { search_fallback_step: searchFallbackStep } : {}),
    ...(shopCategorySlug ? { shop_category: shopCategorySlug } : {}),
  });
}

/**
 * @param {{
 *   resultsSource: string,
 *   hasActiveFilters: boolean,
 *   searchFallbackStep?: number|null,
 *   shopCategorySlug?: string,
 * }} params
 */
export function trackNoResults({
  resultsSource,
  hasActiveFilters,
  searchFallbackStep = null,
  shopCategorySlug = '',
}) {
  sendEvent('no_results', {
    results_source: resultsSource,
    has_active_filters: hasActiveFilters ? 'true' : 'false',
    ...(searchFallbackStep != null ? { search_fallback_step: searchFallbackStep } : {}),
    ...(shopCategorySlug ? { shop_category: shopCategorySlug } : {}),
  });
}

/**
 * @param {string} categorySlug
 * @param {string} linkLocation
 */
export function trackSelectCategory(categorySlug, linkLocation) {
  const slug = typeof categorySlug === 'string' ? categorySlug.trim().toLowerCase() : '';
  sendEvent('select_category', {
    category_slug: slug || 'all',
    link_location: linkLocation,
  });
}

/**
 * @param {string} categorySlug
 * @param {string} subcategoryName
 * @param {string} linkLocation
 */
export function trackSelectSubcategory(categorySlug, subcategoryName, linkLocation) {
  const slug = typeof categorySlug === 'string' ? categorySlug.trim().toLowerCase() : '';
  const name = typeof subcategoryName === 'string' ? subcategoryName.trim() : '';
  if (name === '') {
    return;
  }
  sendEvent('select_subcategory', {
    category_slug: slug,
    subcategory_name: name.length > 64 ? name.slice(0, 64) : name,
    link_location: linkLocation,
  });
}

/**
 * @param {string} categorySlug
 * @param {'home'|'category_hub'} sourcePage
 */
export function trackSeeAllClick(categorySlug, sourcePage) {
  const slug = typeof categorySlug === 'string' ? categorySlug.trim().toLowerCase() : '';
  sendEvent('see_all_click', {
    category_slug: slug,
    source_page: sourcePage,
  });
}

/**
 * @param {{
 *   clickTarget: 'card'|'visit_store_button',
 *   sourcePage: 'search'|'home'|'category_hub',
 *   sourceContext: string,
 *   position?: number|null,
 * }} params
 */
export function trackOutboundClick({
  clickTarget,
  sourcePage,
  sourceContext,
  position = null,
}) {
  sendEvent('outbound_click', {
    click_target: clickTarget,
    source_page: sourcePage,
    source_context: sourceContext,
    position_bucket: bucketPosition(position),
  });
}

/**
 * @param {'search_alert'|'product_price_alert'} leadType
 */
export function trackGenerateLead(leadType) {
  sendEvent('generate_lead', {
    lead_type: leadType,
  });
}

/**
 * @param {string} categorySlug
 */
export function trackCategoryVote(categorySlug) {
  const slug = typeof categorySlug === 'string' ? categorySlug.trim().toLowerCase() : '';
  sendEvent('vote', {
    category_slug: slug || 'unknown',
  });
}

/**
 * @param {string} location
 */
export function trackFacebookFollowClick(location) {
  sendEvent('facebook_follow_click', {
    click_location: location,
  });
}

/**
 * @param {'search_alert'|'product_price_alert'} modalType
 * @param {'search'|'category'|'subcategory'|'product'} scope
 */
export function trackOpenSignupModal(modalType, scope) {
  sendEvent('open_signup_modal', {
    modal_type: modalType,
    scope,
  });
}

/**
 * @param {string} filterType
 * @param {string} [filterValue]
 */
export function trackApplyFilter(filterType, filterValue) {
  sendEvent('apply_filter', {
    filter_type: filterType,
    ...(filterValue ? { filter_value: filterValue } : {}),
  });
}

/**
 * @param {'next'} direction
 * @param {number} pageNumber
 */
export function trackPaginate(direction, pageNumber) {
  sendEvent('paginate', {
    direction,
    page_number: Math.max(1, pageNumber),
  });
}

export function trackFeedbackOpen() {
  sendEvent('feedback_open');
}

export function trackFeedbackSubmit() {
  sendEvent('feedback_submit');
}

export function trackReportProductOpen() {
  sendEvent('report_product_open');
}

/**
 * @param {string} reason
 */
export function trackReportProductSubmit(reason) {
  sendEvent('report_product_submit', {
    reason: typeof reason === 'string' ? reason : 'other',
  });
}

/**
 * @param {'variant1'|'variant2'|'variant3'|'variant4'|'variant5'|'variant6'} copyVariant
 */
export function trackPreLanderCtaClick(copyVariant) {
  sendEvent('pre_lander_cta_click', {
    copy_variant: copyVariant,
  });
}

/**
 * @param {string|undefined} outboundCampaign
 * @param {string|undefined} analyticsSource
 * @returns {'search'|'home'|'category_hub'}
 */
export function resolveGaSourcePage(outboundCampaign, analyticsSource) {
  const ctx = analyticsSource || outboundCampaign || '';
  if (ctx === 'homepage') {
    return 'home';
  }
  if (ctx === 'category') {
    return 'category_hub';
  }
  return 'search';
}
